/**
 * Contact Hooks
 *
 * React Query hooks for contact inquiry management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contactApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { InquiryStatus } from '../types'

// =============================================================================
// Query Keys
// =============================================================================

export const contactKeys = {
  all: ['contact'] as const,
  inquiries: () => [...contactKeys.all, 'inquiries'] as const,
  inquiryList: (filters: Record<string, unknown>) =>
    [...contactKeys.inquiries(), 'list', filters] as const,
  inquiryDetail: (id: number) => [...contactKeys.inquiries(), 'detail', id] as const,
}

// =============================================================================
// Contact Inquiry Hooks
// =============================================================================

export function useContactInquiries(params?: {
  status?: InquiryStatus
  inquiryType?: string
  limit?: number
  offset?: number
}) {
  return useQuery({
    queryKey: contactKeys.inquiryList(params || {}),
    queryFn: () => contactApi.getInquiries(params),
  })
}

export function useContactInquiry(id: number) {
  return useQuery({
    queryKey: contactKeys.inquiryDetail(id),
    queryFn: () => contactApi.getInquiry(id),
    enabled: !!id,
  })
}

export function useUpdateInquiryStatus() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: InquiryStatus }) =>
      contactApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.inquiryDetail(variables.id) })
      queryClient.invalidateQueries({ queryKey: contactKeys.inquiries() })
      toast({
        title: 'Status Updated',
        description: 'The inquiry status has been updated.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update status',
        variant: 'destructive',
      })
    },
  })
}
